import type { ApolloError } from "@apollo/client";
import { useLazyQuery } from "@apollo/client";
import { flatten } from "lodash";
import { useCallback, useEffect, useState } from "react";

import type { IPermissionsContext } from "../types";
import type { GetUserOrganizationsGroupsAtDashboardQuery } from "gql/graphql";
import { GET_USER_ORGANIZATIONS_GROUPS } from "pages/to-do/queries";
import { Logger } from "utils/logger";

type TGroup =
  GetUserOrganizationsGroupsAtDashboardQuery["me"]["organizations"][number]["groups"];
type TGroupsPermissions = Record<string, string[]>;

interface IAllGroupPermissionsResponse {
  error: ApolloError | undefined;
  loading: boolean;
  permissions: TGroupsPermissions;
  permissionMethods: {
    can: (action: string) => boolean;
    canMutate: (action: string) => boolean;
    canResolve: (action: string) => boolean;
    refresh: () => Promise<void>;
  };
  organizations: IPermissionsContext["organizations"];
}

const useAllGroupPermissions = (): IAllGroupPermissionsResponse => {
  const [getAllPermissions, { data: userData, loading, error }] = useLazyQuery(
    GET_USER_ORGANIZATIONS_GROUPS,
    {
      fetchPolicy: "cache-first",
      onError: ({ graphQLErrors }): void => {
        graphQLErrors.forEach((err): void => {
          Logger.warning("An error occurred fetching user groups", err);
        });
      },
    },
  );

  const [allGroupsPermissions, setAllGroupsPermissions] =
    useState<TGroupsPermissions>({});

  const [organizations, setOrganizations] = useState<
    IPermissionsContext["organizations"]
  >([]);

  useEffect((): void => {
    if (userData !== undefined) {
      const orgGroups = userData.me.organizations.flatMap(
        (organization): TGroup => organization.groups,
      );

      const organizationsWithGroups = userData.me.organizations.map(
        (organization): IPermissionsContext["organizations"][number] => ({
          groups: organization.groups,
          name: organization.name,
        }),
      );

      const groupPermissionsMap = orgGroups.reduce<TGroupsPermissions>(
        (acc, group): TGroupsPermissions => ({
          ...acc,
          [group.name]: group.permissions,
        }),
        {},
      );

      setOrganizations(organizationsWithGroups);
      setAllGroupsPermissions(groupPermissionsMap);
    }
  }, [userData]);

  const refresh = useCallback(async (): Promise<void> => {
    await getAllPermissions();
  }, [getAllPermissions]);

  const can = useCallback(
    (permission: string): boolean =>
      flatten(Object.values(allGroupsPermissions)).includes(permission),
    [allGroupsPermissions],
  );

  const canMutate = useCallback(
    (action: string): boolean =>
      can(`integrates_api_mutations_${action}_mutate`),
    [can],
  );

  const canResolve = useCallback(
    (action: string): boolean =>
      can(`integrates_api_resolvers_${action}_resolve`),
    [can],
  );

  return {
    error,
    loading,
    organizations,
    permissionMethods: {
      can,
      canMutate,
      canResolve,
      refresh,
    },
    permissions: allGroupsPermissions,
  };
};

export { useAllGroupPermissions };
